import React, { useEffect } from 'react';
import Lenis from 'lenis';
import Header from '@/components/landing/layout/Header';
import Hero from '@/components/landing/overview/Hero';
import Features from '@/components/landing/common/Features';
import WhyProtega from '@/components/landing/overview/WhyProtega';
import Automation from '@/components/landing/common/Automation';
import ProtectionPurpose from '@/components/landing/overview/ProtectionPurpose';
import ProductShowcase from '@/components/landing/overview/ProductShowcase';
import RedirectButton from '@/components/landing/common/RedirectButton';
import GettingStarted from '@/components/landing/common/GettingStarted';
import Footer from '@/components/landing/layout/Footer';
import { useGSAP } from '@/hooks/useGSAP';

const OverviewPage: React.FC = () => {
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const lenis = new Lenis({
      duration: 1.6,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      smoothWheel: true,
      touchMultiplier: 2,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    const handleBeforeUnload = () => {
      sessionStorage.setItem('scrollY', String(window.scrollY));
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    // 👇 Check if it's a reload (type === 'reload')
    const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    const isReload = navEntries.length > 0 && navEntries[0].type === 'reload';

    if (isReload) {
      const savedScrollY = sessionStorage.getItem('scrollY');
      if (savedScrollY !== null) {
        setTimeout(() => {
          window.scrollTo(0, parseFloat(savedScrollY));
        }, 100); // Wait for Lenis and DOM layout
      }
    }

    return () => {
      lenis.destroy();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useGSAP();

  return (
    <div className="tw-min-h-screen tw-w-full tw-bg-black tw-text-white">
      <Header />
      <main className="tw-relative">
        <div className="tw-h-20 tw-w-full lg:tw-h-24" />
        <Hero />

        <section className="tw-mt-12 tw-mb-12 sm:tw-mt-16 sm:tw-mb-16 md:tw-mt-24 md:tw-mb-24">
          <Features showMainHeading />
        </section>

        <section className="tw-mt-12 tw-mb-12 sm:tw-mt-16 sm:tw-mb-16 md:tw-mt-24 md:tw-mb-24">
          <WhyProtega />
        </section>

        <section className="tw-mt-12 tw-mb-12 sm:tw-mt-16 sm:tw-mb-16 md:tw-mt-24 md:tw-mb-24">
          <ProtectionPurpose />
        </section>

        <section className="tw-mt-12 tw-mb-12 sm:tw-mt-16 sm:tw-mb-16 md:tw-mt-24 md:tw-mb-24">
          <Automation />
        </section>

        <section className="tw-mt-12 tw-mb-12 sm:tw-mt-16 sm:tw-mb-16 md:tw-mt-24 md:tw-mb-24">
          <ProductShowcase />
        </section>

        <section className="tw-mt-12 tw-mb-12 sm:tw-mt-16 sm:tw-mb-16 md:tw-mt-24 md:tw-mb-24">
          <RedirectButton />
        </section>

        <section className="tw-mt-12 tw-mb-12 sm:tw-mt-16 sm:tw-mb-16 md:tw-mt-24 md:tw-mb-24">
          <GettingStarted />
        </section>

        <Footer />
      </main>
    </div>
  );

};

export default OverviewPage;